export function isIncompleteWorkflowStep({ status }: { status: string }): boolean {
  const normalised = status.toLowerCase();
  return normalised !== "completed" && normalised !== "succeeded";
}

export function normaliseWorkflowStatus(status: string): string {
  return status.trim().toLowerCase();
}

export function sentenceCaseWorkflowStatus(status: string): string {
  const normalised = normaliseWorkflowStatus(status).replaceAll("_", " ");
  return normalised ? normalised[0].toUpperCase() + normalised.slice(1) : "Unknown";
}

/** Label a step that has no timeline bar without hiding an already-started state. */
export function untimedWorkflowStepLabel(status: string): string {
  const normalised = normaliseWorkflowStatus(status);
  if (!normalised || ["pending", "queued"].includes(normalised)) return "not started";
  return sentenceCaseWorkflowStatus(normalised);
}

export function displayWorkflowStepStatus(stepStatus: string, parentStatus: string): { status: string; rawStatus: string; interrupted: boolean } {
  const parentEnded = ["failed", "cancelled", "abandoned"].includes(parentStatus.toLowerCase());
  const interrupted = parentEnded && ["running", "paused", "pending", "queued"].includes(stepStatus.toLowerCase());
  return { status: interrupted ? "interrupted" : stepStatus, rawStatus: stepStatus, interrupted };
}

/** Match filters against the status that is actually rendered for a step. */
export function matchesWorkflowStepStatus(stepStatus: string, parentStatus: string, selectedStatuses: readonly string[]): boolean {
  if (selectedStatuses.length === 0) return true;
  const renderedStatus = normaliseWorkflowStatus(displayWorkflowStepStatus(stepStatus, parentStatus).status);
  return selectedStatuses.some((status) => normaliseWorkflowStatus(status) === renderedStatus);
}

export function canonicalWorkflowStepStatuses(statuses: readonly string[]): string[] {
  return [...new Set(statuses.map((status) => normaliseWorkflowStatus(status)).filter(Boolean))].sort();
}

export function workflowStepStatusCsv(statuses: readonly string[]): string {
  return canonicalWorkflowStepStatuses(statuses).join(",");
}

/** A cancelled run leaves queued/running steps without a terminal SDK status. */
export function projectCancelledWorkflowStep<Step extends { status: string }>(step: Step, runStatus: string): Step {
  if (runStatus.toLowerCase() !== "cancelled") return step;
  const status = step.status.toLowerCase();
  if (!["pending", "queued", "running", "paused", "waiting", "sleeping"].includes(status)) return step;
  return { ...step, status: "cancelled" };
}

/**
 * A tree row is successful only when every occurrence in its subtree is
 * successful. Preserve the most actionable status otherwise.
 */
export function aggregateWorkflowStepStatus(statuses: readonly string[]): string {
  const normalised = statuses.map((status) => status.toLowerCase());
  if (normalised.length === 0) return "pending";
  if (normalised.some((status) => status === "failed" || status === "error" || status === "timed_out")) return "failed";
  for (const status of ["interrupted", "running", "waiting", "sleeping", "queued", "paused", "pending"]) {
    if (normalised.includes(status)) return status;
  }
  if (normalised.some((status) => status === "cancelled" || status === "canceled")) return "cancelled";
  const unknown = normalised.find((status) => status !== "completed" && status !== "succeeded");
  return unknown ?? "completed";
}

/** Each node's first status is its newest occurrence; older retries do not win. */
export function aggregateLatestWorkflowNodeStatuses(statusesByNode: readonly (readonly string[])[]): string {
  return aggregateWorkflowStepStatus(statusesByNode.flatMap(([latest]) => latest === undefined ? [] : [latest]));
}

/** Cursor pages can overlap while a run is being refreshed. Keep the newest copy. */
export function uniqueWorkflowSteps<Step extends { stepId: string }>(steps: readonly Step[]): Step[] {
  const seen = new Set<string>();
  return steps.filter((step) => {
    if (seen.has(step.stepId)) return false;
    seen.add(step.stepId);
    return true;
  });
}

export function mergeLatestWorkflowSteps<Step extends { stepId: string; updatedAt: string | null }>(
  preferredSteps: readonly Step[], retainedSteps: readonly Step[],
): Step[] {
  const timestamp = (step: Step) => {
    const parsed = step.updatedAt === null ? Number.NEGATIVE_INFINITY : Date.parse(step.updatedAt);
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  const selected = new Map<string, Step>();
  const order: string[] = [];
  for (const step of [...preferredSteps, ...retainedSteps]) {
    const current = selected.get(step.stepId);
    if (!current) { selected.set(step.stepId, step); order.push(step.stepId); }
    else if (timestamp(step) > timestamp(current)) selected.set(step.stepId, step);
  }
  return order.map((stepId) => selected.get(stepId) as Step);
}

export function retainWorkflowStepBoundary<Step extends { stepId: string; createdAt: string | null }>(
  currentHead: readonly Step[], retainedHead: readonly Step[], hasMore: boolean,
): Step[] {
  if (!hasMore || currentHead.length === 0) return [];
  const time = (step: Step) => { const parsed = step.createdAt === null ? Number.NEGATIVE_INFINITY : Date.parse(step.createdAt); return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY; };
  const compare = (left: Step, right: Step) => time(left) - time(right) || (left.stepId < right.stepId ? -1 : left.stepId === right.stepId ? 0 : 1);
  const oldest = currentHead.reduce((value, step) => compare(step, value) < 0 ? step : value);
  return retainedHead.filter((step) => compare(step, oldest) <= 0);
}

export type WorkflowStepHeadSnapshot<Step> = {
  data: Step[]; cursor: string | null; hasMore: boolean; status?: "warming" | "ready"; coverage?: "warming" | "ready"; retryAfterSeconds?: number;
};

export function mergeWorkflowStepHeadSnapshot<Step extends { stepId: string; createdAt: string | null; updatedAt: string | null }, Page extends WorkflowStepHeadSnapshot<Step>>(
  incoming: Page, previous: Page | null, historicalPagesLoaded: boolean,
): Page {
  const warming = incoming.status === "warming" || incoming.coverage === "warming";
  const retained = warming ? previous?.data ?? [] : retainWorkflowStepBoundary(incoming.data, previous?.data ?? [], incoming.hasMore);
  return { ...incoming, data: mergeLatestWorkflowSteps(incoming.data, retained), cursor: historicalPagesLoaded ? previous?.cursor ?? incoming.cursor : warming ? previous?.cursor ?? incoming.cursor : incoming.cursor, hasMore: warming ? incoming.hasMore || previous?.hasMore === true : incoming.hasMore } as Page;
}

export function mergeWorkflowStepRefreshedPage<Step extends { stepId: string; createdAt: string | null; updatedAt: string | null }, Page extends WorkflowStepHeadSnapshot<Step>>(
  incoming: Page, previous: Page | null, historicalPagesLoaded: boolean,
): Page {
  const merged = mergeWorkflowStepHeadSnapshot(incoming, previous, historicalPagesLoaded);
  return { ...merged, cursor: incoming.cursor, hasMore: incoming.hasMore };
}

/**
 * A BFF tree key identifies one level, not a globally unique node. Encode its
 * complete ancestry so repeated child keys stay beneath their own parent.
 */
export function workflowTreePathKey(ancestorKeys: readonly string[]): string {
  return JSON.stringify(ancestorKeys);
}

/**
 * Overlays a live detail snapshot on all loaded cursor pages without
 * discarding older cached history. Timestamps win so a delayed SSE frame cannot
 * turn a completed step back into a running one.
 */
export function mergeLiveWorkflowSteps<Step extends { stepId: string; updatedAt: string | null }>(
  liveSteps: readonly Step[],
  pagedSteps: readonly Step[],
): Step[] {
  const updatedAt = (step: Step) => {
    const timestamp = step.updatedAt === null ? Number.NEGATIVE_INFINITY : Date.parse(step.updatedAt);
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
  };
  const newer = (candidate: Step, current: Step) => updatedAt(candidate) >= updatedAt(current) ? candidate : current;
  const liveById = new Map<string, Step>();
  for (const step of liveSteps) {
    const current = liveById.get(step.stepId);
    liveById.set(step.stepId, current ? newer(step, current) : step);
  }
  const pagedById = new Map<string, Step>();
  for (const step of pagedSteps) {
    const current = pagedById.get(step.stepId);
    pagedById.set(step.stepId, current ? newer(step, current) : step);
  }

  const merged: Step[] = [];
  const seen = new Set<string>();
  for (const step of liveSteps) {
    if (seen.has(step.stepId)) continue;
    const live = liveById.get(step.stepId) as Step;
    const paged = pagedById.get(step.stepId);
    merged.push(paged ? newer(live, paged) : live);
    seen.add(step.stepId);
  }
  for (const step of pagedSteps) {
    if (seen.has(step.stepId)) continue;
    merged.push(pagedById.get(step.stepId) as Step);
    seen.add(step.stepId);
  }
  return merged;
}

export function shouldFetchNextWorkflowStepPage({
  enabled,
  hasNextPage,
  isFetching,
  isError,
}: {
  enabled: boolean;
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
}): boolean {
  return enabled && hasNextPage && !isFetching && !isError;
}

/**
 * During an incomplete-step crawl, refetching every loaded cursor page blocks
 * the next-page request. Resume normal refreshes once the final page arrives.
 */
export function shouldRefreshWorkflowStepPages({
  loadAll,
  lastPageHasMore,
}: {
  loadAll: boolean;
  lastPageHasMore: boolean;
}): boolean {
  return !loadAll || !lastPageHasMore;
}

export function workflowStepWarmingRefetchInterval(retryAfterSeconds?: number): number {
  const seconds = Number.isFinite(retryAfterSeconds) ? retryAfterSeconds as number : 1;
  return Math.min(30, Math.max(1, seconds)) * 1_000;
}

export function workflowStepHeadRefetchInterval({
  active, status, coverage, retryAfterSeconds,
}: { active: boolean; status?: "warming" | "ready"; coverage?: "warming" | "ready"; retryAfterSeconds?: number }): number | false {
  return status === "warming" || coverage === "warming"
    ? workflowStepWarmingRefetchInterval(retryAfterSeconds)
    : workflowStepRefetchInterval({ active });
}

export function workflowStepRefetchInterval({
  active,
  loadAll,
  lastPageHasMore,
}: {
  active: boolean;
  loadAll?: boolean;
  lastPageHasMore?: boolean;
}): number | false {
  return active && shouldRefreshWorkflowStepPages({ loadAll: loadAll ?? false, lastPageHasMore: lastPageHasMore ?? false }) ? 5_000 : false;
}

export type WorkflowStepExpansion = {
  defaultExpanded: boolean | undefined;
  overrides: Record<string, boolean>;
  /** Defaults scoped to descendants of one encoded workflow tree path. */
  subtreeDefaults: Record<string, boolean>;
};

export const emptyWorkflowStepExpansion = (): WorkflowStepExpansion => ({
  defaultExpanded: undefined,
  overrides: {},
  subtreeDefaults: {},
});

const TREE_KEY_PREFIX = "tree:";
export const WORKFLOW_RUN_TREE_KEY = `${TREE_KEY_PREFIX}[]`;

function treePathParts(key: string): string[] | null {
  if (!key.startsWith(TREE_KEY_PREFIX)) return null;
  try {
    const parts: unknown = JSON.parse(key.slice(TREE_KEY_PREFIX.length));
    return Array.isArray(parts) && parts.every((part) => typeof part === "string") ? parts : null;
  } catch {
    return null;
  }
}

function treeAncestorKeys(key: string): string[] {
  const parts = treePathParts(key);
  if (!parts) return [];
  return Array.from(
    { length: Math.max(0, parts.length - 1) },
    (_, index) => `${TREE_KEY_PREFIX}${JSON.stringify(parts.slice(0, index + 1))}`,
  ).reverse();
}

function isTreeDescendant(parentKey: string, key: string): boolean {
  const parent = treePathParts(parentKey);
  const candidate = treePathParts(key);
  return parent !== null
    && candidate !== null
    && candidate.length > parent.length
    && parent.every((part, index) => candidate[index] === part);
}

export function workflowStepExpanded(
  expansion: WorkflowStepExpansion,
  key: string,
  fallback: boolean,
): boolean {
  const override = expansion.overrides[key];
  if (override !== undefined) return override;
  for (const ancestorKey of treeAncestorKeys(key)) {
    const subtreeDefault = expansion.subtreeDefaults[ancestorKey];
    if (subtreeDefault !== undefined) return subtreeDefault;
  }
  return expansion.defaultExpanded ?? fallback;
}

export function withWorkflowStepExpansionOverride(
  expansion: WorkflowStepExpansion,
  key: string,
  expanded: boolean,
): WorkflowStepExpansion {
  return { ...expansion, overrides: { ...expansion.overrides, [key]: expanded } };
}

export function withWorkflowStepExpansionDefault(expanded: boolean): WorkflowStepExpansion {
  return { defaultExpanded: expanded, overrides: {}, subtreeDefaults: {} };
}

/**
 * Set one tree branch's descendant preference without affecting siblings.
 * Existing child choices and nested branch defaults are deliberately reset so
 * the branch action also applies to future pages and live children.
 */
export function withWorkflowStepDescendantExpansion(
  expansion: WorkflowStepExpansion,
  parentKey: string,
  expanded: boolean,
): WorkflowStepExpansion {
  const withoutDescendantOverrides = Object.fromEntries(
    Object.entries(expansion.overrides).filter(([key]) => !isTreeDescendant(parentKey, key)),
  );
  const withoutDescendantDefaults = Object.fromEntries(
    Object.entries(expansion.subtreeDefaults).filter(
      ([key]) => key !== parentKey && !isTreeDescendant(parentKey, key),
    ),
  );
  return {
    ...expansion,
    overrides: { ...withoutDescendantOverrides, [parentKey]: true },
    subtreeDefaults: { ...withoutDescendantDefaults, [parentKey]: expanded },
  };
}
